/**
 * Line cards carry the blocking scheme and nothing else. A lineman reads his
 * rule off the card and the play call tells him which scheme; the backs and
 * receivers would only be clutter at wristband size. So a line card leaves off
 * every skill player and every route one of them runs.
 *
 * Only the printed card changes. The play itself keeps every player, so the
 * same play still prints complete as a skill card, and the editor still shows
 * them (faded) so they can be selected and moved.
 */
import type { PlayData, PlayPlayer } from '../types';
import { resolveActors } from './animate';
import { PB_LINE_ICON_SCALE, PB_SKILL_TYPES, pbIsLineman } from './constants';

export function lineCardPlay(play: PlayData): PlayData {
  if (!play.players.some((p) => PB_SKILL_TYPES.has(p.type))) return play; // nothing to drop: same object, same output
  const actors = resolveActors(play);
  const skill = (i: number) => i >= 0 && PB_SKILL_TYPES.has(play.players[i]!.type);
  return {
    ...play,
    players: play.players.filter((p) => !PB_SKILL_TYPES.has(p.type)),
    lines: play.lines.filter((_, i) => !skill(actors[i]!)),
  };
}

/**
 * Line cards draw every icon PB_LINE_ICON_SCALE times bigger (legible on a
 * wristband), but formations are spaced for normal icons, so a formation
 * loaded onto a line card came in with the linemen piled on top of each other.
 * This widens the line about its middle, horizontally only, until neighbours
 * on the line of scrimmage clear each other, keeping everyone on the field.
 * Players that aren't linemen, and anything already spaced wide enough, are
 * returned untouched.
 */
export function spreadForLineCard(players: PlayPlayer[], playType?: string): PlayPlayer[] {
  const idx = players.map((p, i) => (pbIsLineman(p.type) ? i : -1)).filter((i) => i >= 0);
  if (idx.length < 2) return players;
  // Icon diameter as a fraction of play width: radius is 5% of the condensed
  // width, and a spread field is twice as wide for the same icon size.
  const diam = 2 * 0.05 * PB_LINE_ICON_SCALE * (playType === 'spread' ? 0.5 : 1);
  const want = diam * 1.12;
  const ys = idx.map((i) => players[i]!.y).sort((a, b) => a - b);
  const midY = ys[Math.floor(ys.length / 2)]!;
  // Only the men on the line set the spacing (a pulling guard stacked behind
  // the line would otherwise read as a zero gap).
  const row = idx.filter((i) => Math.abs(players[i]!.y - midY) < diam * 0.8).map((i) => players[i]!.x).sort((a, b) => a - b);
  if (row.length < 2) return players;
  let minGap = Infinity;
  for (let k = 1; k < row.length; k++) minGap = Math.min(minGap, row[k]! - row[k - 1]!);
  if (minGap >= want * 0.98 || minGap <= 0.001) return players; // 2%: rounding slack
  const xs = idx.map((i) => players[i]!.x);
  const c = players.find((p) => p.type === 'C')?.x ?? (Math.min(...xs) + Math.max(...xs)) / 2;
  const edge = diam / 2 + 0.01;
  const reach = Math.max(c - Math.min(...xs), Math.max(...xs) - c, 1e-6);
  const room = Math.min(c - edge, 1 - edge - c);
  const k = Math.max(1, Math.min(want / minGap, room / reach));
  if (k === 1) return players;
  return players.map((p) => (pbIsLineman(p.type) ? { ...p, x: Math.round((c + (p.x - c) * k) * 10000) / 10000 } : p));
}

/** True when switching this play to a line card would hide part of what is drawn. */
export function needsOwnLineCard(play: PlayData): boolean {
  return play.players.some((p) => PB_SKILL_TYPES.has(p.type)) || play.lines.length > 0;
}

/** Just the linemen, spaced for line-card icons. */
export function lineCardPlayers(players: PlayPlayer[], playType?: string): PlayPlayer[] {
  return spreadForLineCard(players.filter((p) => pbIsLineman(p.type)), playType);
}

/**
 * A new line card made from a skill play: the same line in the same spots
 * (spaced for the bigger icons), no skill players, no blocks yet — blocks
 * drawn at skill-card spacing would no longer start at their man.
 */
export function lineCardFrom(play: PlayData, id: string): PlayData {
  return {
    id,
    name: `${play.name || 'Play'} (line)`,
    playType: play.playType,
    cardMode: 'line',
    players: lineCardPlayers(play.players, play.playType),
    lines: [],
    ...(play.category ? { category: play.category } : {}),
  };
}
